/**
 * 認証コンテキスト
 *
 * アプリケーション全体で認証状態を管理
 * LaravelでいうAuth Facadeの機能をReactで実現
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { api, ApiError } from '../services/api';
import type { User, LoginRequest, LoginResponse } from '../types';

/**
 * 認証コンテキストの型定義
 */
interface AuthContextType {
  /** 現在のユーザー */
  user: User | null;
  /** ログイン中かどうか */
  isAuthenticated: boolean;
  /** 初期化中かどうか（トークン検証中） */
  isLoading: boolean;
  /** ログイン処理 */
  login: (credentials: LoginRequest) => Promise<User>;
  /** ログアウト処理 */
  logout: () => Promise<void>;
  /** ユーザー情報を更新 */
  refreshUser: () => Promise<void>;
}

// コンテキストの作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ローカルストレージのキー
const TOKEN_KEY = 'token';
const USER_CACHE_KEY = 'authUser';

function getCachedUser(): User | null {
  const cached = localStorage.getItem(USER_CACHE_KEY);
  if (!cached) {
    return null;
  }

  try {
    return JSON.parse(cached) as User;
  } catch {
    localStorage.removeItem(USER_CACHE_KEY);
    return null;
  }
}

function cacheUser(user: User): void {
  localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
}

function clearAuthStorage(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_CACHE_KEY);
}

async function clearApiCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  await caches.delete('api-cache');
}

/**
 * 認証プロバイダー
 *
 * アプリケーションのルートでこのプロバイダーでラップすることで、
 * 子コンポーネントから認証状態にアクセス可能になる
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * トークンからユーザー情報を取得
   */
  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      return null;
    }

    try {
      const userData = await api.get<User>('/api/auth/me');
      cacheUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to fetch current user:', error);

      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearAuthStorage();
        return null;
      }

      return getCachedUser();
    }
  }, []);

  /**
   * 初期化時にトークンを検証
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const currentUser = await fetchCurrentUser();
        setUser(currentUser);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [fetchCurrentUser]);

  /**
   * ログイン処理
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<User> => {
    const response = await api.post<LoginResponse>('/api/auth/login', credentials);

    // トークンを保存
    localStorage.setItem(TOKEN_KEY, response.token);
    cacheUser(response.user);

    // ユーザー情報をセット
    setUser(response.user);
    return response.user;
  }, []);

  /**
   * ログアウト処理
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // サーバーにログアウトを通知（オプション）
      await api.post('/api/auth/logout');
    } catch (error) {
      // エラーが発生しても続行
      console.error('Logout API error:', error);
    } finally {
      // トークンを削除
      clearAuthStorage();
      clearApiCaches().catch((error) => {
        console.error('Failed to clear API cache:', error);
      });
      // ユーザー情報をクリア
      setUser(null);
    }
  }, []);

  /**
   * ユーザー情報を更新
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    const currentUser = await fetchCurrentUser();
    setUser(currentUser);
  }, [fetchCurrentUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 認証コンテキストを使用するためのカスタムフック
 *
 * 使用例:
 * const { user, login, logout, isAuthenticated } = useAuth();
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
